import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, FileCheck, Search, Users, Clock, Award, CheckCircle, Star, ChevronLeft, ChevronRight, Zap, TrendingUp, Globe, Lock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import ContactPopup from '../components/ContactPopup';
import ProcessSlider from '../components/ProcessSlider';
import { services, testimonials, faqs, processSteps, stats } from '../mock';

const Home = () => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [itemsPerView, setItemsPerView] = useState(3);

  // Update items per view based on screen size
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setItemsPerView(3); // Desktop
      } else if (window.innerWidth >= 768) {
        setItemsPerView(2); // Tablet
      } else {
        setItemsPerView(1); // Mobile
      }
    };

    handleResize(); // Initial check
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Calculate max valid index based on items per view
  const maxTestimonialIndex = testimonials.length - itemsPerView;

  // Auto-slide testimonials every 5 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTestimonial((prev) => {
        return prev < maxTestimonialIndex ? prev + 1 : 0; // Loop back to start
      });
    }, 5000);

    return () => clearInterval(timer);
  }, [maxTestimonialIndex]);

  const nextTestimonial = () => {
    setCurrentTestimonial((prev) => {
      return prev < maxTestimonialIndex ? prev + 1 : prev;
    });
  };

  const prevTestimonial = () => {
    setCurrentTestimonial((prev) => Math.max(prev - 1, 0));
  };
  return (
    <div className="min-h-screen bg-slate-950 overflow-hidden">
      {/* Hero Section - Enhanced with split design */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        {/* Animated background gradients */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-950 to-slate-900" />
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-amber-500/5 to-transparent" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl" />
        <div className="absolute top-20 right-20 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl" />
        
        {/* Geometric decorative elements */}
        <div className="absolute top-10 right-1/4 w-20 h-20 border-2 border-white/20 rotate-45 animate-pulse" />
        <div className="absolute bottom-20 left-1/4 w-32 h-32 border border-white/10 rounded-full" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left content */}
            <div className="space-y-8">
              <div className="inline-flex items-center bg-gradient-to-r from-amber-500/20 to-amber-600/20 border border-white/20 rounded-full px-6 py-3 backdrop-blur-sm">
                <Award className="h-5 w-5 text-amber-400 mr-2" />
                <span className="text-sm text-amber-400 font-semibold">Trusted by 5000+ Businesses Nationwide</span>
              </div>
              
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black text-white leading-tight pb-2">
                Protect Your
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-amber-400 via-amber-500 to-amber-600 mt-2 pb-2 leading-snug">
                  Brand Legacy
                </span>
              </h1>
              
              <p className="text-xl text-gray-300 leading-relaxed">
                Navigate intellectual property with confidence. Expert trademark registration, copyright protection, and business formation services across the United States.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 items-center sm:items-start justify-center sm:justify-start">
                <Button 
                  size="lg" 
                  onClick={() => setIsPopupOpen(true)}
                  className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-bold shadow-2xl shadow-amber-500/25 transform transition-all hover:scale-105 hover:shadow-amber-500/40 px-8 py-6 text-lg w-full sm:w-auto"
                >
                  Start Protection Now
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Link to="/services" className="w-full sm:w-auto">
                  <Button size="lg" variant="outline" className="border-2 border-white/20 text-white hover:bg-amber-500/10 hover:border-white/40 hover:shadow-lg hover:shadow-amber-500/20 hover:scale-[1.02] backdrop-blur-sm px-8 py-6 text-lg font-semibold w-full">
                    Explore Services
                  </Button>
                </Link>
              </div>
            </div>
            
            {/* Right content - Feature highlights */}
            <div className="hidden lg:grid grid-cols-2 gap-4">
              {[
                { icon: Shield, title: "98% Success", desc: "Approval Rate", color: "from-blue-500 to-blue-600" },
                { icon: Zap, title: "24-48 Hours", desc: "Fast Filing", color: "from-amber-500 to-amber-600" },
                { icon: Globe, title: "Nationwide", desc: "Coverage", color: "from-green-500 to-green-600" },
                { icon: Lock, title: "Secure", desc: "IP Protection", color: "from-purple-500 to-purple-600" }
              ].map((feature, idx) => (
                <div key={idx} className="group relative bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-sm border border-white/20/50 rounded-2xl p-6 hover:border-white/40 hover:shadow-lg hover:shadow-amber-500/20 hover:scale-[1.02] transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-amber-500/10">
                  <div className={`bg-gradient-to-br ${feature.color} w-12 h-12 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg`}>
                    <feature.icon className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-white font-bold text-lg mb-1">{feature.title}</h3>
                  <p className="text-gray-400 text-sm">{feature.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Informative Section - Below Hero */}
      <section className="py-16 relative bg-gradient-to-b from-slate-950 to-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left - Information */}
            <div>
              <div className="inline-block mb-4">
                <span className="text-amber-500 font-semibold uppercase tracking-wider text-sm">Why Trademark Protection Matters</span>
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-6 leading-tight">
                Your Brand is Your Most
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600"> Valuable Asset</span>
              </h2>
              <p className="text-gray-300 text-lg leading-relaxed mb-6">
                In today's competitive marketplace, protecting your brand identity is not just important—it's essential. A registered trademark gives you exclusive rights to your brand name, logo, and tagline, preventing competitors from using similar marks that could confuse your customers.
              </p>
              <p className="text-gray-400 text-base leading-relaxed mb-8">
                Without trademark protection, you risk losing your brand identity, customer trust, and market position. Our expert team has helped over 5,000 businesses secure their trademarks with a 98% approval rate, ensuring your brand remains uniquely yours.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 items-center sm:items-start justify-center sm:justify-start">
                <Button 
                  onClick={() => setIsPopupOpen(true)}
                  className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-bold shadow-lg w-full sm:w-auto"
                >
                  Protect Your Brand Today
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Link to="/about" className="w-full sm:w-auto">
                  <Button variant="outline" className="border-2 border-white/60 text-white hover:bg-white/10 w-full">
                    Learn More About Us
                  </Button>
                </Link>
              </div>
            </div>

            {/* Right - Key Points */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {[
                {
                  icon: Shield,
                  title: "Legal Protection",
                  desc: "Exclusive rights to use your brand nationwide with full legal backing"
                },
                {
                  icon: TrendingUp,
                  title: "Business Growth",
                  desc: "Increase brand value and attract investors with registered IP"
                },
                {
                  icon: Globe,
                  title: "Market Dominance",
                  desc: "Prevent competitors from copying your brand identity"
                },
                {
                  icon: Award,
                  title: "Consumer Trust",
                  desc: "Build credibility with the ® symbol showing legitimate business"
                }
              ].map((item, idx) => (
                <div key={idx} className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-sm border border-white/20 rounded-xl p-6 hover:border-white/40 hover:shadow-lg hover:shadow-white/10 transition-all">
                  <div className="bg-gradient-to-br from-amber-500/20 to-amber-600/20 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                    <item.icon className="h-6 w-6 text-amber-400" />
                  </div>
                  <h3 className="text-white font-bold text-lg mb-2">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section - Enhanced with cards */}
      <section className="py-20 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          {/* Desktop Grid */}
          <div className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-6">
            {stats.map((stat, index) => (
              <div key={index} className="relative group">
                <div className="absolute inset-0 bg-gradient-to-br from-amber-500/20 to-amber-600/20 rounded-2xl blur-xl group-hover:blur-2xl transition-all opacity-0 group-hover:opacity-100" />
                <div className="relative bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-sm border border-white/20/50 rounded-2xl p-8 text-center hover:border-white/40 hover:shadow-lg hover:shadow-amber-500/20 hover:scale-[1.02] transition-all duration-300 hover:scale-105">
                  <div className="text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600 mb-2">{stat.number}</div>
                  <div className="text-gray-400 font-medium uppercase text-xs tracking-wider">{stat.label}</div>
                </div>
              </div>
            ))}
          </div>
          
          {/* Mobile Slider */}
          <div className="md:hidden overflow-x-auto scrollbar-hide">
            <div className="flex gap-4 pb-4" style={{ scrollSnapType: 'x mandatory' }}>
              {stats.map((stat, index) => (
                <div key={index} className="flex-shrink-0 w-[280px]" style={{ scrollSnapAlign: 'center' }}>
                  <div className="relative group">
                    <div className="absolute inset-0 bg-gradient-to-br from-amber-500/20 to-amber-600/20 rounded-2xl blur-xl" />
                    <div className="relative bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-sm border border-white/20/50 rounded-2xl p-8 text-center">
                      <div className="text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600 mb-2">{stat.number}</div>
                      <div className="text-gray-400 font-medium uppercase text-xs tracking-wider">{stat.label}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services Section - Asymmetric Grid */}
      <section className="py-24 relative bg-slate-950">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-amber-500/50 to-transparent" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <div className="inline-block mb-4">
              <span className="text-amber-500 font-semibold uppercase tracking-wider text-sm">Our Expertise</span>
            </div>
            <h2 className="text-4xl sm:text-5xl font-black text-white mb-6">
              Comprehensive IP Protection
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600">Services</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              End-to-end intellectual property solutions tailored for modern businesses
            </p>
          </div>

          {/* Desktop Grid */}
          <div className="hidden md:grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => {
              const IconComponent = {
                shield: Shield,
                'file-text': FileCheck,
                building: Users,
                search: Search,
                'shopping-bag': Award
              }[service.icon];

              return (
                <div key={service.id} className={`group relative ${index === 0 ? 'lg:col-span-2 lg:row-span-1' : ''}`}>
                  <div className="absolute inset-0 bg-gradient-to-br from-amber-500/10 to-amber-600/5 rounded-3xl blur-xl group-hover:blur-2xl transition-all opacity-0 group-hover:opacity-100" />
                  <Card className="relative bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-sm border-white/20/50 hover:border-white/40 hover:shadow-lg hover:shadow-amber-500/20 hover:scale-[1.02] transition-all duration-500 hover:scale-[1.02] h-full overflow-hidden">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-amber-500/10 to-transparent rounded-bl-full" />
                    <CardHeader className="relative">
                      <div className="bg-gradient-to-br from-amber-500/20 to-amber-600/20 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-6 transition-all shadow-lg shadow-amber-500/20">
                        <IconComponent className="h-8 w-8 text-amber-400" />
                      </div>
                      <CardTitle className="text-white text-2xl font-bold mb-3">{service.title}</CardTitle>
                      <CardDescription className="text-gray-300 text-base">{service.shortDesc}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-3 mb-6">
                        {service.features.slice(0, 4).map((feature, idx) => (
                          <li key={idx} className="flex items-start text-gray-300 text-sm group/item">
                            <CheckCircle className="h-5 w-5 text-amber-500 mr-3 flex-shrink-0 mt-0.5 group-hover/item:scale-110 transition-transform" />
                            <span className="leading-relaxed">{feature}</span>
                          </li>
                        ))}
                      </ul>
                      <Link to={service.slug}>
                        <Button variant="ghost" className="text-amber-400 hover:text-amber-300 hover:bg-amber-500/10 w-full justify-between group/btn border border-white/20 hover:border-white/50">
                          <span className="font-semibold">Explore Service</span>
                          <ArrowRight className="h-5 w-5 group-hover/btn:translate-x-1 transition-transform" />
                        </Button>
                      </Link>
                    </CardContent>
                  </Card>
                </div>
              );
            })}
          </div>
          
          {/* Mobile Slider */}
          <div className="md:hidden overflow-x-auto scrollbar-hide">
            <div className="flex gap-4 pb-4" style={{ scrollSnapType: 'x mandatory' }}>
              {services.map((service) => {
                const IconComponent = {
                  shield: Shield,
                  'file-text': FileCheck,
                  building: Users,
                  search: Search,
                  'shopping-bag': Award
                }[service.icon];

                return (
                  <div key={service.id} className="flex-shrink-0 w-[320px]" style={{ scrollSnapAlign: 'center' }}>
                    <div className="group relative h-full">
                      <div className="absolute inset-0 bg-gradient-to-br from-amber-500/10 to-amber-600/5 rounded-3xl blur-xl" />
                      <Card className="relative bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-sm border-white/20/50 h-full overflow-hidden">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-amber-500/10 to-transparent rounded-bl-full" />
                        <CardHeader className="relative">
                          <div className="bg-gradient-to-br from-amber-500/20 to-amber-600/20 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-amber-500/20">
                            <IconComponent className="h-8 w-8 text-amber-400" />
                          </div>
                          <CardTitle className="text-white text-xl font-bold mb-3">{service.title}</CardTitle>
                          <CardDescription className="text-gray-300 text-sm">{service.shortDesc}</CardDescription>
                        </CardHeader>
                        <CardContent>
                          <ul className="space-y-2 mb-6">
                            {service.features.slice(0, 4).map((feature, idx) => (
                              <li key={idx} className="flex items-start text-gray-300 text-xs">
                                <CheckCircle className="h-4 w-4 text-amber-500 mr-2 flex-shrink-0 mt-0.5" />
                                <span className="leading-relaxed">{feature}</span>
                              </li>
                            ))}
                          </ul>
                          <Link to={service.slug}>
                            <Button variant="ghost" className="text-amber-400 hover:text-amber-300 hover:bg-amber-500/10 w-full justify-between border border-white/20">
                              <span className="font-semibold text-sm">Explore Service</span>
                              <ArrowRight className="h-4 w-4" />
                            </Button>
                          </Link>
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-slate-900/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Our Streamlined Process
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              We make trademark registration simple with our proven five-step process.
            </p>
          </div>

          {/* Desktop Grid */}
          <div className="hidden md:grid grid-cols-1 md:grid-cols-3 gap-8">
            {processSteps.map((step, index) => (
              <div key={step.step} className="relative">
                <div className="bg-slate-900 border border-white/20 rounded-xl p-8 h-full hover:border-white/40 hover:shadow-xl hover:shadow-white/10 hover:scale-[1.02] transition-all group">
                  <div className="bg-gradient-to-br from-amber-500 to-amber-600 text-white w-16 h-16 rounded-full flex items-center justify-center font-black text-2xl mb-6 shadow-xl group-hover:scale-110 transition-transform">
                    {step.step}
                  </div>
                  <h3 className="text-white font-bold text-xl mb-3">{step.title}</h3>
                  <p className="text-gray-400 leading-relaxed">{step.description}</p>
                </div>
                {index < processSteps.length - 1 && (
                  <ArrowRight className="hidden md:block absolute -right-4 top-1/2 transform -translate-y-1/2 text-amber-500 h-8 w-8 z-10" />
                )}
              </div>
            ))}
          </div>
          
          {/* Mobile Auto-Slider */}
          <ProcessSlider steps={processSteps} />
        </div>
      </section>

      {/* Pricing Packages */}
      <section className="py-20 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Choose Your Package
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Transparent pricing with no hidden fees. All packages include USPTO filing fees.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
            {[
              {
                name: 'Deluxe',
                price: '$299',
                description: 'For brands needing extra support, including help with USPTO office actions.',
                note: '+ Federal Filing Fee',
                features: [
                  'Case Review',
                  'Case Filing',
                  'Direct Hit Search',
                  'Trademark Alert',
                  'Trademark Secured',
                  'High Satisfaction Rate',
                  'Refusal Risk Meter',
                  'Trademark Monitoring',
                  'Complete Documentation',
                  'Digital File',
                  'Dedicated Case Manager'
                ],
                popular: true,
                recommended: true,
                disclaimer: '(Govt. filing fee not included)'
              },
              {
                name: 'Platinum',
                price: '$349',
                description: 'All-inclusive plan with monitoring, renewals, and full legal assistance.',
                note: '+ Federal Filing Fee',
                features: [
                  'Case Review',
                  'Case Filing',
                  'Direct Hit Search',
                  'Trademark Alert',
                  'Trademark Secured',
                  'High Satisfaction Rate',
                  'Refusal Risk Meter',
                  'Trademark Monitoring',
                  'Complete Documentation',
                  'Digital File',
                  'Dedicated Case Manager',
                  'High Approval Rate',
                  'Comprehensive Trademark Search Report (Federal & State)',
                  'High Priority Service',
                  'Cease & Desist Letter'
                ],
                disclaimer: '(Govt. filing fee not included)'
              },
              {
                name: 'Abandoned Application Refiling',
                price: '$699',
                description: 'Complete refiling solution for abandoned trademark applications with 100% approval guarantee.',
                note: '+ Federal Filing Fee',
                features: [
                  'Trademark Search & Risk Assessment',
                  'Case Review & Filing by a U.S. Registered IP Attorney',
                  'Federal Search & Clearance Report (All States)',
                  'Unlimited office action responses',
                  'FREE Attorney Consultation',
                  'Monitoring Services for 5 yrs',
                  'Application Drafting & Submission',
                  'Filing an Objection Letter (if required)',
                  'Copy of Cease & Desist Letter',
                  '100% Approval Guarantee',
                  'Private Registration (Optional)'
                ],
                disclaimer: '(Govt. filing fee not included)'
              }
            ].map((pkg) => (
              <Card key={pkg.name} className={`bg-slate-900/60 border-white/20 relative ${pkg.popular ? 'border-white/60 shadow-xl shadow-white/10' : 'hover:border-white/40 hover:shadow-lg hover:shadow-amber-500/20 hover:scale-[1.02]'} transition-all duration-300 flex flex-col h-[600px]`}>
                {pkg.recommended && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10">
                    <span className="bg-gradient-to-r from-amber-500 to-amber-600 text-white text-xs font-bold px-4 py-1 rounded-full">
                      RECOMMENDED
                    </span>
                  </div>
                )}
                <CardHeader className="text-center pb-4 flex-shrink-0">
                  <CardTitle className="text-white text-xl mb-2">{pkg.name}</CardTitle>
                  <div className="text-3xl font-bold text-amber-500 mb-1">{pkg.price}</div>
                  <div className="text-xs text-gray-500 mb-2">{pkg.note}</div>
                  <CardDescription className="text-gray-400 text-sm leading-relaxed">{pkg.description}</CardDescription>
                </CardHeader>
                <CardContent className="flex flex-col flex-grow">
                  <div className="flex-grow overflow-y-auto mb-4 pr-2" style={{ maxHeight: '250px' }}>
                    <ul className="space-y-2">
                      {pkg.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start text-gray-300 text-xs">
                          <CheckCircle className="h-3 w-3 text-amber-500 mr-2 flex-shrink-0 mt-0.5" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="flex-shrink-0">
                    <p className="text-xs text-gray-500 text-center mb-4">{pkg.disclaimer}</p>
                    <Button 
                      onClick={() => setIsPopupOpen(true)}
                      className={`w-full ${pkg.popular ? 'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700' : 'bg-slate-800 hover:bg-slate-700'} text-white font-semibold`}
                    >
                      Register My Trademark
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us - Split layout with visual elements */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-950 to-slate-900" />
        <div className="absolute right-0 top-0 w-1/3 h-full bg-gradient-to-l from-amber-500/5 to-transparent" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left - Content */}
            <div>
              <div className="inline-block mb-4">
                <span className="text-amber-500 font-semibold uppercase tracking-wider text-sm">Why Choose Us</span>
              </div>
              <h2 className="text-4xl sm:text-5xl font-black text-white mb-6 leading-tight">
                Dedicated IP Professionals
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600">You Can Trust</span>
              </h2>
              <p className="text-gray-300 text-lg mb-12 leading-relaxed">
                Experience unmatched expertise, speed, and support in protecting your intellectual property with industry-leading success rates.
              </p>
              
              <Button 
                size="lg" 
                onClick={() => setIsPopupOpen(true)}
                className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-bold shadow-2xl shadow-amber-500/25 hover:shadow-amber-500/40 px-8"
              >
                Start Your Journey
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
            
            {/* Right - Feature cards stacked (Desktop) */}
            <div className="hidden lg:block space-y-6">
              {[
                { 
                  icon: Users, 
                  title: "Expert Team", 
                  desc: "Experienced attorneys with 98% approval rate and thousands of successful registrations",
                  color: "from-blue-500 to-blue-600"
                },
                { 
                  icon: Clock, 
                  title: "Fast Turnaround", 
                  desc: "Priority filing within 24-48 hours without compromising quality or accuracy",
                  color: "from-amber-500 to-amber-600"
                },
                { 
                  icon: Shield, 
                  title: "Comprehensive Support", 
                  desc: "End-to-end protection from consultation to registration and ongoing maintenance",
                  color: "from-green-500 to-green-600"
                }
              ].map((feature, idx) => (
                <div key={idx} className="group relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-amber-500/10 to-transparent rounded-2xl blur-xl group-hover:blur-2xl transition-all" />
                  <div className="relative bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-sm border border-white/20/50 rounded-2xl p-6 hover:border-white/40 hover:shadow-lg hover:shadow-amber-500/20 hover:scale-[1.02] transition-all duration-300 hover:scale-[1.02]">
                    <div className="flex items-start gap-5">
                      <div className={`bg-gradient-to-br ${feature.color} w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform shadow-lg`}>
                        <feature.icon className="h-7 w-7 text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-white font-bold text-xl mb-2">{feature.title}</h3>
                        <p className="text-gray-400 leading-relaxed">{feature.desc}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Mobile Slider */}
            <div className="lg:hidden overflow-x-auto scrollbar-hide">
              <div className="flex gap-4 pb-4" style={{ scrollSnapType: 'x mandatory' }}>
                {[
                  { 
                    icon: Users, 
                    title: "Expert Team", 
                    desc: "Experienced attorneys with 98% approval rate and thousands of successful registrations",
                    color: "from-blue-500 to-blue-600"
                  },
                  { 
                    icon: Clock, 
                    title: "Fast Turnaround", 
                    desc: "Priority filing within 24-48 hours without compromising quality or accuracy",
                    color: "from-amber-500 to-amber-600"
                  },
                  { 
                    icon: Shield, 
                    title: "Comprehensive Support", 
                    desc: "End-to-end protection from consultation to registration and ongoing maintenance",
                    color: "from-green-500 to-green-600"
                  }
                ].map((feature, idx) => (
                  <div key={idx} className="flex-shrink-0 w-[320px]" style={{ scrollSnapAlign: 'center' }}>
                    <div className="group relative">
                      <div className="absolute inset-0 bg-gradient-to-br from-amber-500/10 to-transparent rounded-2xl blur-xl" />
                      <div className="relative bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-sm border border-white/20/50 rounded-2xl p-6">
                        <div className="flex items-start gap-4">
                          <div className={`bg-gradient-to-br ${feature.color} w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg`}>
                            <feature.icon className="h-6 w-6 text-white" />
                          </div>
                          <div className="flex-1">
                            <h3 className="text-white font-bold text-lg mb-2">{feature.title}</h3>
                            <p className="text-gray-400 text-sm leading-relaxed">{feature.desc}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials - Auto-slide */}
      <section className="py-12 md:py-20 bg-slate-950">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 md:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3 md:mb-4">
              What Our Clients Say
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-400 max-w-2xl mx-auto">
              Join thousands of satisfied clients who trust us with their intellectual property.
            </p>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative px-0 md:px-16 lg:px-20">
            <div className="overflow-hidden -mx-4 sm:-mx-6 lg:-mx-8">
              {/* Testimonial Cards Container - Responsive */}
              <div 
                className="flex transition-transform duration-700 ease-in-out"
                style={{ 
                  transform: `translateX(-${currentTestimonial * (100 / itemsPerView)}%)`
                }}
              >
                {testimonials.map((testimonial, index) => (
                  <div key={index} className="min-w-full md:min-w-[50%] lg:min-w-[33.333%] w-full md:w-1/2 lg:w-1/3 flex-shrink-0 px-4 sm:px-6 lg:px-8">
                    <Card className="bg-gradient-to-br from-slate-900/80 to-slate-800/80 backdrop-blur-sm border border-white/20 rounded-2xl p-4 md:p-6 h-full hover:border-white/40 hover:shadow-xl hover:shadow-white/10 transition-all">
                      <CardContent className="pt-4 md:pt-6">
                        <div className="flex items-center justify-center mb-4 md:mb-6">
                          {[...Array(testimonial.rating)].map((_, i) => (
                            <Star key={i} className="h-4 w-4 md:h-5 md:w-5 lg:h-6 lg:w-6 text-amber-500 fill-amber-500 mx-0.5" />
                          ))}
                        </div>
                        <p className="text-gray-300 text-sm sm:text-base md:text-lg text-center mb-4 md:mb-6 italic leading-relaxed">
                          "{testimonial.content}"
                        </p>
                        <div className="text-center border-t border-white/20 pt-3 md:pt-4">
                          <p className="text-white font-bold text-sm md:text-base">{testimonial.name}</p>
                          <p className="text-gray-400 text-xs md:text-sm mt-1">{testimonial.role}, {testimonial.company}</p>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                ))}
              </div>
            </div>

            {/* Navigation Arrows - Hidden on mobile */}
            <button
              onClick={prevTestimonial}
              className="hidden md:block absolute left-0 top-1/2 -translate-y-1/2 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white p-3 rounded-full shadow-xl transition-all hover:scale-110 z-10"
              aria-label="Previous testimonial"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
            <button
              onClick={nextTestimonial}
              className="hidden md:block absolute right-0 top-1/2 -translate-y-1/2 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white p-3 rounded-full shadow-xl transition-all hover:scale-110 z-10"
              aria-label="Next testimonial"
            >
              <ChevronRight className="h-6 w-6" />
            </button>

            {/* Dots Navigation - Show dots for valid positions only */}
            <div className="flex justify-center gap-2 md:gap-3 mt-8">
              {Array.from({ length: maxTestimonialIndex + 1 }).map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentTestimonial(index)}
                  className={`h-2 md:h-3 rounded-full transition-all ${
                    index === currentTestimonial 
                      ? 'bg-amber-500 w-8 md:w-12' 
                      : 'bg-slate-700 w-2 md:w-3 hover:bg-slate-600'
                  }`}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>

            {/* Progress Bar */}
            <div className="mt-6 bg-slate-800 rounded-full h-1 overflow-hidden max-w-md mx-auto">
              <div 
                className="bg-gradient-to-r from-amber-500 to-amber-600 h-full transition-all duration-700 ease-in-out"
                style={{ width: `${((currentTestimonial + 1) / testimonials.length) * 100}%` }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section - Creative Design */}
      <section className="py-20 relative overflow-hidden bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
        {/* Decorative elements */}
        <div className="absolute top-10 right-10 w-64 h-64 bg-amber-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-10 left-10 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl" />
        
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <div className="inline-block mb-4">
              <span className="text-amber-500 font-semibold uppercase tracking-wider text-sm">Got Questions?</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-4">
              Frequently Asked
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600">Questions</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Everything you need to know about our trademark registration services.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            {faqs.map((faq, index) => (
              <Accordion key={faq.id} type="single" collapsible>
                <AccordionItem value={`item-${faq.id}`} className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-sm border border-white/20 rounded-2xl px-6 py-2 hover:border-white/40 hover:shadow-xl hover:shadow-white/10 transition-all group">
                  <AccordionTrigger className="text-white font-bold text-left hover:text-amber-400 py-6 group-hover:scale-[1.01] transition-all">
                    <div className="flex items-start gap-4">
                      <div className="bg-gradient-to-br from-amber-500/20 to-amber-600/20 w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                        <span className="text-amber-400 font-black text-lg">{index + 1}</span>
                      </div>
                      <span className="flex-1">{faq.question}</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-400 pl-14 pr-4 pb-6 leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            ))}
          </div>

          {/* CTA below FAQ */}
          <div className="mt-12 text-center">
            <p className="text-gray-300 text-lg mb-6">Still have questions? We're here to help!</p>
            <Button 
              onClick={() => setIsPopupOpen(true)}
              className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-bold shadow-xl hover:shadow-amber-500/30 hover:scale-105 transition-all px-8"
            >
              Contact Our Experts
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Industry Coverage Section */}
      <section className="py-20 bg-gradient-to-b from-slate-950 to-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-block mb-4">
              <span className="text-amber-500 font-semibold uppercase tracking-wider text-sm">Industry Expertise</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-4">
              We Protect Brands Across
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600">All Industries</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              From startups to established enterprises, we've secured trademarks for businesses in every sector.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {[
              { icon: "🛍️", name: "E-Commerce" },
              { icon: "🏥", name: "Healthcare" },
              { icon: "💼", name: "Professional Services" },
              { icon: "🍔", name: "Food & Beverage" },
              { icon: "💻", name: "Technology" },
              { icon: "🎨", name: "Creative Arts" },
              { icon: "🏗️", name: "Construction" },
              { icon: "👗", name: "Fashion" },
              { icon: "🎓", name: "Education" },
              { icon: "🏨", name: "Hospitality" },
              { icon: "📱", name: "Mobile Apps" },
              { icon: "🔧", name: "Manufacturing" }
            ].map((industry, idx) => (
              <div key={idx} className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 backdrop-blur-sm border border-white/20 rounded-xl p-6 hover:border-white/40 hover:shadow-lg hover:shadow-white/10 hover:scale-105 transition-all text-center group">
                <div className="text-4xl mb-3 group-hover:scale-110 transition-transform">{industry.icon}</div>
                <div className="text-white font-semibold text-sm">{industry.name}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner - Eye-catching */}
      <section className="py-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-amber-500/20 via-amber-600/10 to-amber-500/20" />
        <div className="absolute inset-0 backdrop-blur-sm" />
        <div className="absolute top-0 left-0 w-full h-full">
          <div className="absolute top-1/2 left-1/4 w-96 h-96 bg-amber-500/20 rounded-full blur-3xl animate-pulse" />
          <div className="absolute top-1/2 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse delay-1000" />
        </div>
        
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="bg-gradient-to-br from-slate-900/80 to-slate-800/80 backdrop-blur-sm border-2 border-white/60 rounded-3xl p-12 shadow-2xl shadow-amber-500/20">
            <div className="inline-flex items-center bg-green-500/20 border border-green-500/40 rounded-full px-6 py-2 mb-6">
              <CheckCircle className="h-5 w-5 text-green-400 mr-2" />
              <span className="text-green-400 font-bold text-sm">Limited Time: Free Trademark Search Included!</span>
            </div>
            
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-6 leading-tight">
              Start Your Trademark Protection
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600">Today & Save Time</span>
            </h2>
            
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              Join thousands of successful businesses who have protected their brands with our expert guidance and 98% approval rate.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg"
                onClick={() => setIsPopupOpen(true)}
                className="bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white text-sm sm:text-base md:text-lg font-black shadow-2xl hover:shadow-amber-500/50 hover:scale-110 transition-all px-6 sm:px-8 md:px-10 py-5 sm:py-6 md:py-7"
              >
                <span className="whitespace-normal leading-tight text-center">
                  Register My Trademark Now
                </span>
                <ArrowRight className="ml-2 sm:ml-3 h-5 w-5 sm:h-6 sm:w-6 flex-shrink-0" />
              </Button>
              <Button 
                size="lg"
                variant="outline"
                onClick={() => {
                  if (window.zE) {
                    window.zE('webWidget', 'open');
                  }
                }}
                className="border-2 border-white/60 text-white hover:bg-white/10 text-lg font-semibold px-10 py-7 chat-button"
              >
                Chat with an Expert
              </Button>
            </div>
            
            <div className="mt-8 flex items-center justify-center gap-8 text-sm text-gray-400">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-400" />
                <span>No Upfront Payment</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-400" />
                <span>98% Approval Rate</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-400" />
                <span>Expert Support</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <ContactPopup isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} />
    </div>
  );
};

export default Home;
